import { useState } from "react";
import { ChevronDown, ChevronRight, Wallet } from "lucide-react";
import { Skeleton } from "../ui/skeleton";

// From the ONE list — this file held its own copy of the type list and its
// own label map, which is the #78 drift shape in miniature. Two types
// (P5/P8): sickness is a reason under CL, not a type.
import { LEAVE_TYPES, LEAVE_TYPE_LABEL } from "../../lib/leaveOptions";
import { count, plural } from "../../lib/vocabulary";

const TYPES = LEAVE_TYPES.map((t) => t.value);

/**
 * Entitlement / consumed / balance for everyone the viewer can see.
 *
 * Collapsed by default. The number that actually informs an approve decision is
 * already on the pending row itself; this is the wider picture for when someone
 * asks "how much leave has Nitin taken this year?", and it should not push the
 * queue below the fold to answer a question nobody asked yet.
 */
export default function LeaveBalancesPanel({ balances, loading = false }) {
  const [open, setOpen] = useState(false);

  if (!loading && (!balances || balances.length === 0)) return null;

  return (
    <section className="bg-white border border-callisto rounded-xl" data-testid="leave-balances-panel">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-4 py-3 text-left"
        data-testid="leave-balances-toggle"
        aria-expanded={open}
      >
        {open ? <ChevronDown size={16} className="text-callisto-navy/60" />
              : <ChevronRight size={16} className="text-callisto-navy/60" />}
        <Wallet size={14} className="text-callisto-gold" />
        <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-callisto-gold">
          Leave balances
        </span>
        <span className="text-xs text-callisto-muted">
          {loading ? "…" : plural(balances.length, "person", "people")}
        </span>
      </button>

      {open && (
        <div className="px-4 pb-4 overflow-x-auto">
          {loading ? (
            <Skeleton className="h-24 rounded-lg" />
          ) : (
            <table className="w-full text-xs min-w-[520px]" data-testid="leave-balances-table">
              <thead>
                <tr className="text-callisto-muted border-b border-callisto">
                  <th className="text-left font-semibold py-2">Employee</th>
                  {TYPES.map((t) => (
                    <th key={t} className="text-right font-semibold py-2 px-2">
                      {LEAVE_TYPE_LABEL[t]}
                    </th>
                  ))}
                  <th className="text-right font-semibold py-2 px-2">PL lapsed</th>
                  <th className="text-right font-semibold py-2 pl-2">LOP</th>
                </tr>
              </thead>
              <tbody>
                {balances.map((row) => (
                  <tr
                    key={row.user_id}
                    className="border-b border-callisto/50 last:border-0"
                    data-testid={`leave-balance-row-${row.user_id}`}
                  >
                    <td className="py-2 font-semibold text-callisto-navy">{row.user_name}</td>
                    {TYPES.map((t) => (
                      <td key={t} className="py-2 px-2 text-right tabular-nums">
                        {/* Missing is missing (Pattern A), the same rule the
                            LOP cell below has always applied: a type this row
                            does not carry is unreadable, not exhausted. It
                            also used to throw — `row[t].balance` on an absent
                            type is a TypeError, which took the whole panel
                            down rather than the one number. */}
                        <span className="font-bold text-callisto-navy">
                          {count(row[t]?.balance)}
                        </span>
                        <span className="text-callisto-muted"> / {count(row[t]?.accrued)}</span>
                      </td>
                    ))}
                    <td className="py-2 px-2 text-right tabular-nums">
                      {/* Days forfeited at the 45-day ceiling — visible per
                          P5/P8, not recoverable, and worth a Director's eye. */}
                      {row.PL?.lapsed_ytd > 0
                        ? <span className="font-bold text-orange-700" data-testid={`pl-lapsed-${row.user_id}`}>{row.PL.lapsed_ytd}</span>
                        : <span className="text-callisto-muted">—</span>}
                    </td>
                    <td className="py-2 pl-2 text-right tabular-nums">
                      {/* LOP has no entitlement, only days taken — showing it as
                          "0 / 0" would imply one exists. */}
                      {row.LOP?.used > 0
                        ? <span className="font-bold text-orange-700">{row.LOP.used}</span>
                        : <span className="text-callisto-muted">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <p className="mt-2 text-[10px] text-callisto-muted">
            Balance / accrued for the current year. PL accrues from working days
            on duty; days beyond its ceiling lapse. LOP is days taken without balance.
          </p>
        </div>
      )}
    </section>
  );
}
